/**
 * Extract the registrable-ish hostname from a URL string.
 *
 * Lowercases the host and strips a leading `www.` so that
 * `https://www.koalaliving.com.au/x` and `koalaliving.com.au/y` collapse to
 * the same group key. Returns null when the input is empty or unparseable.
 *
 * Note: this is not a real public-suffix-list lookup — `koalaliving.com.au`
 * and `support.koalaliving.com.au` would not collapse. Good enough for Karl's
 * use case; revisit if a supplier uses a subdomain regularly.
 */
export function extractHostname(url: string | null | undefined): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (trimmed.length === 0) return null;
  try {
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const u = new URL(withProtocol);
    return u.hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
}

export type GroupKind = 'host' | 'vendor' | 'none';

/**
 * Single source of truth for the Consolidation view's grouping logic.
 *
 * Tries the product URL hostname first; falls back to the vendor field
 * (case-insensitive); finally drops items in an `Uncategorised` bucket so
 * nothing is silently hidden.
 */
export function normaliseGroupKey(item: {
  productUrl: string | null;
  vendor: string | null;
}): { key: string; label: string; kind: GroupKind } {
  const host = extractHostname(item.productUrl);
  if (host) return { key: `host:${host}`, label: host, kind: 'host' };
  const vendor = (item.vendor ?? '').trim();
  if (vendor) {
    return {
      key: `vendor:${vendor.toLowerCase()}`,
      label: vendor,
      kind: 'vendor',
    };
  }
  return { key: 'none', label: 'Uncategorised', kind: 'none' };
}

/** Resolve a relative-ish product URL to something the browser can navigate to. */
export function resolveProductHref(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}
